// MergeHint — contextual one-liner under each merge-mode radio in Configure.
//
// Reads the cached org inventory (counts + detected pack) and tells the user
// what *this specific* merge mode will do given their org's current state
// and their requested pack/volume. Resolves the recurring confusion where
// users picked Smart-merge after a Tiny load and were surprised the org
// didn't grow to Medium-volume targets.
//
// Hint matrix:
//
//   mode = additive
//     empty org      -> "Loads ~N records from scratch."
//     same pack      -> "Adds ~N more records on top of your existing data."
//     different pack -> "⚠ Will mix N pack records with your existing M pack data."
//
//   mode = smart  (Recommended; default)
//     empty org      -> "Same as Additive — both load from scratch."
//     same pack ≥ target -> "You already have enough. Will skip generators that hit
//                            their target (~15 min faster than Additive)."
//     same pack < target -> "Will load the additional ~N to reach Medium-volume target."
//     different pack -> "⚠ This org has X data. Smart loads new pack on top — pick
//                        Clean to start fresh."
//
//   mode = clean
//     empty org      -> "Same as Additive — nothing to clean."
//     anything else  -> "Wipes ~N existing records, then loads ~M fresh."

import type { OrgInventory } from "@/lib/types";

interface MergeHintProps {
  mode: "additive" | "smart" | "clean";
  inventory: OrgInventory | null;
  requestedPackId: string | null;
  requestedVolumeAccountTarget: number;
  requestedVolumeName: string;
}

function isEmpty(inv: OrgInventory): boolean {
  return Object.values(inv.counts).every((c) => c === 0);
}

function totalRecords(inv: OrgInventory): number {
  return Object.values(inv.counts).reduce((a, b) => a + b, 0);
}

export function MergeHint({
  mode,
  inventory,
  requestedPackId,
  requestedVolumeAccountTarget,
  requestedVolumeName,
}: MergeHintProps): JSX.Element | null {
  if (!inventory) return null;

  const accounts = inventory.counts.Account ?? 0;
  const total = totalRecords(inventory);
  const empty = isEmpty(inventory);
  // Only treat detected pack as "different" if the confidence is high enough
  // to bother the user about it. Below 0.5 the signal is too noisy to act on.
  const isDifferentPack =
    !empty
    && requestedPackId !== null
    && inventory.detected_pack_id !== null
    && inventory.detected_pack_id !== requestedPackId
    && inventory.detected_pack_confidence >= 0.5;

  let text: string;
  let warn = false;

  if (mode === "additive") {
    if (empty) {
      text = `Loads ~${requestedVolumeAccountTarget.toLocaleString()} Accounts from scratch.`;
    } else if (isDifferentPack) {
      text = `Will mix new ${requestedPackId} records with your existing ${inventory.detected_pack_id} data (~${total.toLocaleString()} records).`;
      warn = true;
    } else {
      text = `Adds ~${requestedVolumeAccountTarget.toLocaleString()} more Accounts on top of your existing ~${accounts.toLocaleString()}.`;
    }
  } else if (mode === "smart") {
    if (empty) {
      text = "Same as Additive on an empty org — both load from scratch.";
    } else if (isDifferentPack) {
      text = `This org has ${inventory.detected_pack_id} data. Smart will load ${requestedPackId} on top — pick Clean for a single-pack org.`;
      warn = true;
    } else if (accounts >= requestedVolumeAccountTarget) {
      text = `You already have ~${accounts.toLocaleString()} Accounts (>= ${requestedVolumeName} target). Smart will skip generators that hit their target — ~15 min faster than Additive.`;
    } else {
      const delta = Math.max(
        0,
        requestedVolumeAccountTarget - accounts,
      );
      text = `Will load the additional ~${delta.toLocaleString()} Accounts to reach the ${requestedVolumeName} target (~${requestedVolumeAccountTarget.toLocaleString()}).`;
    }
  } else {
    if (empty) {
      text = "Same as Additive — nothing to clean.";
    } else {
      text = `Wipes ~${total.toLocaleString()} existing records, then loads ~${requestedVolumeAccountTarget.toLocaleString()} Accounts fresh.`;
      warn = true;
    }
  }

  return (
    <div
      style={{
        marginTop: 8,
        padding: "6px 8px",
        borderRadius: 6,
        background: warn ? "rgba(234, 88, 12, .08)" : "rgba(0, 0, 0, .04)",
        color: warn ? "#9A3412" : "var(--ink-2)",
        fontSize: 10.5,
        lineHeight: 1.4,
        textAlign: "left",
      }}
    >
      {warn ? "⚠ " : ""}
      {text}
    </div>
  );
}
